import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { EntityMentionType, type LlmChatEntityReferenceDto } from '@paladin-monorepo/common';
import { cn } from '@/lib/utils';

interface LlmChatEntityReferenceProps {
  reference: LlmChatEntityReferenceDto;
}

const PREFIX_KEYS: Record<EntityMentionType, string> = {
  [EntityMentionType.Inquiry]: 'llmChat.mentions.types.inquiry',
  [EntityMentionType.Offer]: 'llmChat.mentions.types.offer',
  [EntityMentionType.Project]: 'llmChat.mentions.types.project',
  [EntityMentionType.DecisionCase]: 'llmChat.mentions.types.decisionCase',
  [EntityMentionType.CaseStudy]: 'llmChat.mentions.types.caseStudy',
};

/**
 * Tints the surface rather than naming a colour, and inherits the surrounding text colour.
 *
 * A reference sits inside a message bubble whose background differs by role - the user's is
 * `bg-primary`, the assistant's is `bg-muted` - so any fixed colour is unreadable on one of
 * them. `text-primary` on `bg-primary/10` was exactly that: the brand colour on itself.
 * This follows the inline-code treatment in `MarkdownContent`, which has the same problem.
 */
const chipClassName =
  'mx-0.5 inline-flex max-w-full items-baseline gap-1 rounded bg-black/10 px-1.5 py-0.5 align-baseline text-sm text-inherit dark:bg-white/15';

/**
 * A sent `@` reference.
 *
 * Deliberately a router `Link` rather than a markdown link: `MarkdownContent` renders every
 * anchor with `target="_blank"`, which would leave the app for an internal record.
 */
export function LlmChatEntityReference({ reference }: LlmChatEntityReferenceProps) {
  const { t } = useTranslation();

  const prefix = t(PREFIX_KEYS[reference.type], { defaultValue: reference.type });
  const name = reference.name?.trim() || t('llmChat.mentions.untitled');
  const label = `${prefix}: ${name}`;

  // Looked up and gone: say so visibly.
  if (!reference.available) {
    return (
      <span
        className={cn(chipClassName, 'line-through opacity-70')}
        title={t('llmChat.mentions.unavailable')}
      >
        {label}
      </span>
    );
  }

  // Available but with nowhere to point yet - render it plainly rather than as deleted.
  if (!reference.url) {
    return <span className={chipClassName}>{label}</span>;
  }

  return (
    <Link
      to={reference.url}
      className={cn(
        chipClassName,
        'underline underline-offset-2 hover:bg-black/20 dark:hover:bg-white/20',
      )}
    >
      {label}
    </Link>
  );
}
